// Calls the Function API with the signed-in user's token. The API authorizes every call itself;
// what the web app hides or shows is convenience, never the control.

import { useCallback, useEffect, useState } from "react";
import type {
  AccessConfig,
  ClientBilling,
  RevenueType,
  Capability,
  CategoryMapping,
  CapacityInput,
  DataScope,
  EntryFlag,
  Exclusion,
  BonusOn,
  BonusOverrideRecord,
  BonusTargetRecord,
  BonusType,
  CostRates,
  PersonWeeks,
  ProfitabilityInput,
  ProfitScenario,
  Project,
  ProjectStatus,
  HolidayYearDoc,
  Person,
  RateCard,
  RateStep,
  Role,
  RoleRate,
  RoleRateOverride,
  TaxTableDoc,
  PayType,
  RoleTarget,
  TargetAssumptions,
} from "../../core";
import { getToken } from "./auth";
import { appConfig, MOCK } from "./config";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/** Whom an admin is viewing Base Camp as, for this browser tab (an admin's convenience; the API checks every call). */
export interface ViewAs {
  kind: "person" | "level";
  id: string;
  name: string;
  /** A person viewed at a level the admin chose (Chris, 2026-10-19). */
  levelId?: string;
  levelName?: string;
}

const VIEW_AS_KEY = "basecamp.viewAs";

export function viewingAs(): ViewAs | null {
  try {
    const raw = sessionStorage.getItem(VIEW_AS_KEY);
    return raw ? (JSON.parse(raw) as ViewAs) : null;
  } catch {
    return null;
  }
}

function setViewingAs(v: ViewAs | null) {
  try {
    if (v) sessionStorage.setItem(VIEW_AS_KEY, JSON.stringify(v));
    else sessionStorage.removeItem(VIEW_AS_KEY);
  } catch {
    // Storage blocked: View as can't persist across the reload, so it simply doesn't take effect.
  }
}

/** Starts viewing as a person or a level: the API checks and logs it, then the app reloads as them. */
export async function startViewAs(kind: ViewAs["kind"], id: string, levelId?: string) {
  setViewingAs(null);
  const { viewAs } = await send<{ viewAs: ViewAs }>("POST", "/view-as", { kind, id, ...(levelId ? { levelId } : {}) });
  setViewingAs(viewAs);
  window.location.hash = "#/";
  window.location.reload();
}

/** Stops viewing as: logged by the API, then the app reloads as the admin, back on the Access screen. */
export async function stopViewAs() {
  const v = viewingAs();
  setViewingAs(null);
  if (v) await send("POST", "/view-as/stop", v).catch(() => undefined);
  window.location.hash = "#/access";
  window.location.reload();
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const v = viewingAs();
  if (v) init = { ...init, headers: { ...init.headers, "X-View-As": `${v.kind}:${encodeURIComponent(v.id)}${v.levelId ? `:${encodeURIComponent(v.levelId)}` : ""}` } };
  if (MOCK) {
    const { mockApi } = await import("./mock");
    const reply = await mockApi(path, init);
    const body = reply.body as { error?: string };
    if (reply.status >= 400) throw new ApiError(reply.status, body.error ?? "Request failed");
    return reply.body as T;
  }
  const token = await getToken();
  const res = await fetch(`${appConfig.apiBaseUrl}${path}`, {
    ...init,
    headers: { ...init.headers, Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body.error ?? res.statusText);
  return body as T;
}

export const send = <T>(method: "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown) =>
  api<T>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });

export const errorText = (err: unknown) => (err instanceof Error ? err.message : "Something went wrong.");

/** Loads a GET endpoint; `reload` fetches again. A null path loads nothing. */
export function useApi<T>(path: string | null) {
  const [state, setState] = useState<{ data?: T; error?: string; loading: boolean }>({ loading: !!path });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!path) return;
    let live = true;
    setState((s) => ({ ...s, loading: true, error: undefined }));
    api<T>(path)
      .then((data) => live && setState({ data, loading: false }))
      .catch((err) => live && setState({ error: errorText(err), loading: false }));
    return () => {
      live = false;
    };
  }, [path, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}

// --- Response shapes --------------------------------------------------------------------------------

export type Stored<T> = T & { _etag: string };

export interface Me {
  name: string;
  email: string;
  levels: { id: string; name: string }[];
  capabilities: string[];
  scope: "all" | "own-projects" | "own-rows";
  /** Present while an admin views Base Camp as someone else. */
  viewingAs?: ViewAs;
  /** Expense reports are on once their receipt storage exists (PRD ER5). */
  features?: { expenses: boolean; receiptAi?: boolean };
}

export interface PeopleResponse {
  asOf?: string;
  people: (Stored<Person> & { rateCard?: RateCard })[];
}

export interface PersonResponse {
  person: Stored<Person>;
  rateCard: RateCard;
  rateHistory: { base: RateStep[]; ote: RateStep[] };
}

export interface ReferenceResponse {
  taxTables: Stored<TaxTableDoc & { id: string }>[];
  holidays: Stored<HolidayYearDoc & { id: string }>[];
  roles: Stored<{ id: "roles"; roles: Role[] }>;
  settings: CompanySettings;
  bonusTypes: BonusType[];
}

export interface RoleRatesResponse {
  asOf: string;
  roleRates: RoleRate[];
  overrides: RoleRateOverride[];
  /** Target salaries per role, and each role's target rate on the day (§3.12). */
  targets: RoleTarget[];
  targetRates: Record<string, { rate: number; oteRate: number; type: PayType; loaded: number; loadedOte: number; productiveHours: number; warnings: string[] } | null>;
  targetAssumptions: TargetAssumptions;
  etag: string | null;
}

export interface PreviewResponse {
  day: string;
  before: { baseRate: number; oteRate: number; loadedBase: number; loadedOte: number; productiveHours: number };
  after: { baseRate: number; oteRate: number; loadedBase: number; loadedOte: number; productiveHours: number };
  warnings: string[];
}

export interface HistoryResponse {
  entries: { at: string; who: string; action: string; summary?: string }[];
}

export interface BonusRow {
  id: string;
  name: string;
  title?: string;
  roleId?: string;
  status: string;
  endDate?: string;
  _etag: string;
  pay: { type: string; amount: number; annual: number } | null;
  bonus: BonusOn;
  ote: number | null;
  oteNow: number | null;
  bonusTargets: BonusTargetRecord[];
  bonusOverrides: BonusOverrideRecord[];
  /** BP5: no pay on file, or an amount on a retired type. */
  warnings: string[];
}

export interface BonusDashboardResponse {
  asOf: string;
  /** Archived people left out; shown with ?archived=1. */
  archivedCount?: number;
  types: BonusType[];
  typesEtag: string | null;
  people: BonusRow[];
  totals: { byType: { typeId: string; target: number; amount: number; people: number }[]; target: number; amount: number; overrides: number };
  retiredPlans: { name: string; year: number; mode: string; pool: number; participants: number }[];
}

export interface ProjectsDashboardResponse {
  asOf: string;
  financial: boolean;
  /** The company target; a project may set its own. */
  targetMargin: number;
  window: { from: string; to: string };
  counts: Record<string, number>;
  pipeline: { status: ProjectStatus; count: number; value?: number; weighted?: number }[];
  money: null | {
    months: { month: string; booked: number; pipeline: number; pipelineFull: number; cost: number }[];
    kpis: { booked12: number; pipeline12: number; bookedMargin12: number | null; backlog: number; belowTarget: number; measured: number };
    projects: { id: string; code: string; name: string; status: ProjectStatus; revenue: number; cost: number; contributionPct: number | null; targetMargin: number; months: Record<string, { revenue: number; cost: number }> }[];
  };
  load: { weekStarts: string[]; people: { personId: string; name: string; weeks: (number | null)[]; average: number | null; hours: number }[]; thresholds: { over: number; under: number }; counted: string[] };
}

export interface ProfitabilityResponse extends ProfitabilityInput {
  asOf: string;
  targetMargin: number;
  scenarios: Stored<ProfitScenario>[];
}

export interface CapacityResponse extends CapacityInput {
  end: string;
  thresholds: { over: number; under: number };
  roles: { id: string; name: string }[];
}

export interface AccessResponse extends AccessConfig {
  version: string;
  catalog: { id: Capability; grants: string }[];
  scopes: { id: DataScope; text: string }[];
  yourGroups: string[];
  history: { at: string; who: string; summary: string }[];
}

/** /people?view=names: no pay, for switchers and importers. */
export interface PeopleNamesResponse {
  people: { id: string; name: string; roleId?: string; status: string; endDate?: string; payIds?: string[] }[];
}

export interface CompanySettings {
  targetMargin: number;
  overheadPerHour: number;
  loadThresholds: { over: number; under: number };
  /** AC4: the 1st of the month the calendar sync takes over. */
  actualsCutover: string;
  /** How target salaries are burdened (§3.12, rev. 1.16). */
  targetAssumptions: TargetAssumptions;
  _etag?: string | null;
}

export interface ProjectSummary {
  id: string;
  code: string;
  name: string;
  clientName: string;
  status: ProjectStatus;
  probability?: number;
  closedOn?: string;
  billable: boolean;
  isTemplate: boolean;
  ownerName?: string;
  startMonday: string;
  end: string;
  weeks: number;
  hours: number;
  clientFees?: number;
  revenue?: number;
  labor?: number;
  expenses?: number;
  contributionPct?: number | null;
  priceForTarget?: number;
  targetMargin?: number;
}

export interface ProjectsResponse {
  projects: ProjectSummary[];
}

export interface ProjectResponse {
  project: Stored<Project>;
  /** owner: may hand the project to someone else (sees every project and may edit plans). */
  access: { financial: boolean; pay: boolean; edit: boolean; owner: boolean };
  costRates?: CostRates;
  holidays: string[];
  settings?: CompanySettings;
  people: { id: string; name: string; roleId?: string; status: string; endDate?: string; hasEmail?: boolean }[];
  roles: Role[];
  /** The client's payment terms and invoice table (CF1–CF2); with financials.read. */
  clientBilling?: ClientBilling & { set: boolean };
  partnerTerms?: Partial<Record<RevenueType, number>>;
}

export interface LoadResponse {
  startMonday: string;
  weeks: number;
  people: Record<string, PersonWeeks>;
  counted: string[];
}

export interface CodesResponse {
  /** retired: a project closed before 2026, kept so it isn't reused; it takes nothing new. */
  /** billable: billed to a client; internal (3S001–3S003), time off and non-billable projects aren't. */
  codes: { code: string; client: string; dba?: string; status?: string; retired?: boolean; billable?: boolean }[];
}

export interface ActualsResponse {
  cutover: string;
  weeks: { personId: string; monday: string; state: "open" | "frozen"; hours: number; byCode: Record<string, number>; review: number }[];
}

export interface InboxItem {
  personId: string;
  monday: string;
  etag: string;
  entryId: string;
  day: string;
  start: string;
  end: string;
  hours: number;
  categories: string[];
  codes: string[];
  flags: EntryFlag[];
  /** week: an under-reported week (AC9); timeoff: requested time off with nothing on the calendar (2026-10-29). */
  source: "import" | "graph" | "manual" | "week" | "timeoff";
  /** An under-reported week (AC9): the hours expected; `hours` is what was reported. */
  expected?: number;
  /** Changed in Outlook after the week froze (P2b, AC1): settled with accept or keep. */
  outlook?: { kind: "deleted" | "changed" | "added"; text: string; seen: string };
  subject?: string;
}

/** One person's result from a calendar sync run (P2b). */
export interface PersonSyncResult {
  personId: string;
  name: string;
  status: "ok" | "denied" | "no-mailbox" | "error" | "no-email";
  code?: number;
  weeks: number;
  added: number;
  updated: number;
  removed: number;
  flagged: number;
  at: string;
}

export interface SyncStatusResponse {
  cutover: string;
  window: { from: string; to: string } | null;
  lastRun: { at: string; by: string; from: string; to: string; people: PersonSyncResult[] } | null;
  byPerson: Record<string, PersonSyncResult>;
}

export interface SyncRunResponse {
  cutover: string;
  window: { from: string; to: string } | null;
  people: PersonSyncResult[];
}

export interface CompareResponse {
  person: { id: string; name: string };
  weeks: { monday: string; recorded: { hours: number; byCode: Record<string, number> }; calendar: { hours: number; byCode: Record<string, number>; review: number } }[];
  excluded: Record<string, number>;
}

export interface InboxResponse {
  /** True for a member: only their own items, and no settling. */
  own?: boolean;
  items: InboxItem[];
  heldBack: number;
  /** For an admin: the codes each listed day's hours can be re-coded to (2026-10-28). */
  codesByDay?: Record<string, string[]>;
}

export interface ImportActualsResult {
  weeks: number;
  entries: number;
  hours: number;
  excluded: Partial<Record<Exclusion, number>>;
  afterCutover: number;
}

export interface CategoryIndexResponse {
  mappings: CategoryMapping[];
  copiedFromListAt: string | null;
  etag: string | null;
}

export interface CategoryIndexSaved extends CategoryIndexResponse {
  recounted: number;
}

export interface CategoryListResponse {
  columns: { name: string; displayName: string }[];
  rows: Record<string, string>[];
}
